import { useState, useCallback } from 'react';
import {
  maintenanceService,
  MaintenanceImpactSlide,
  ImpactSlidePayload,
} from '../services/maintenanceService';
import { useToast } from './index';

export const useMaintenanceImpactSlides = () => {
  const { success, error } = useToast();
  const [slides, setSlides] = useState<MaintenanceImpactSlide[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);

  const fetchSlides = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await maintenanceService.getImpactSlides();
      if (response?.success) {
        const sorted = [...response.data].sort(
          (a, b) => a.display_order - b.display_order
        );
        setSlides(sorted);
      }
    } catch (err: any) {
      error(err?.message || 'Failed to fetch impact slides');
    } finally {
      setIsLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const createSlide = useCallback(
    async (payload: ImpactSlidePayload): Promise<boolean> => {
      setIsSaving(true);
      try {
        await maintenanceService.createImpactSlide(payload);
        success('Slide created successfully');
        await fetchSlides();
        return true;
      } catch (err: any) {
        error(err?.message || 'Failed to create slide');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [fetchSlides] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const updateSlide = useCallback(
    async (
      slideId: string,
      payload: Partial<ImpactSlidePayload>
    ): Promise<boolean> => {
      setIsSaving(true);
      try {
        await maintenanceService.updateImpactSlide(slideId, payload);
        success('Slide updated successfully');
        await fetchSlides();
        return true;
      } catch (err: any) {
        error(err?.message || 'Failed to update slide');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [fetchSlides] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const deleteSlide = useCallback(
    async (slideId: string): Promise<boolean> => {
      setIsDeletingId(slideId);
      try {
        await maintenanceService.deleteImpactSlide(slideId);
        success('Slide deleted');
        setSlides(prev => prev.filter(s => s.id !== slideId));
        return true;
      } catch (err: any) {
        error(err?.message || 'Failed to delete slide');
        return false;
      } finally {
        setIsDeletingId(null);
      }
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    slides,
    isLoading,
    isSaving,
    isDeletingId,
    fetchSlides,
    createSlide,
    updateSlide,
    deleteSlide,
  };
};
